import { Component, computed, inject, Injector, input, OnInit } from '@angular/core';
import {
  DocLineDeletedDto,
  DocLineMovedDto,
  DocLineRole,
  DocLineUpdatedDto,
  StockDocMovementDto,
  StockDocMovementService,
} from '@digiwall/goliath-documents/proxy';
import StockDocLinesEditorComponent, {
  StockDocLinesImports,
  StockDocLinesProviders,
} from '../../../stock-doc-lines-editor.component';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallback,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { map, Observable, of } from 'rxjs';
import { LocalizationService } from '@abp/ng.core';
import { ObserverQueue, ShortcutKey } from '@digiwall/core';
import { StorageLocationService } from '@digiwall/goliath-core/proxy';
import { StockDocLineEditorHelperService } from '../../../../../components';
import {
  IStockMovementLinesEditorComponent,
  StockDocMovementPolicy,
  StockLinePolicy,
} from './stock-doc-movement-lines-policies/stock-doc-movement-line.policy';
import { Router } from '@angular/router';
import { LabelPrinterHelper } from '@digiwall/goliath-products';
import { StockDocWithdrawalMovementPolicy } from './stock-doc-movement-lines-policies/stock-doc-withdrawal-movement-line.policy';
import { ArticleSerialService, ArticleService } from '@digiwall/goliath-products/proxy';
import { StockDocEntryMovementPolicy } from './stock-doc-movement-lines-policies/stock-doc-entry-movement-line.policy';

@Component({
  selector: 'document-stock-doc-movement-lines-grid',
  templateUrl: './stock-doc-movement-lines-editor.component.html',
  imports: StockDocLinesImports,
  providers: StockDocLinesProviders,
})
export default class StockDocMovementLinesEditorComponent
  extends StockDocLinesEditorComponent<StockDocMovementDto, StockDocMovementService>
  implements IStockMovementLinesEditorComponent
{
  public readonly copyLineData = input.required<boolean>();
  public readonly printLabel = input.required<boolean>();

  private labelPrinterHelper = inject(LabelPrinterHelper);

  private _policy!: StockLinePolicy;

  private get policy(): StockLinePolicy {
    return this._policy;
  }

  protected override get articleLineRole() {
    return DocLineRole.StockMovementArticleLine;
  }
  public get stockLocalizationService(): LocalizationService {
    return this.localizationService;
  }
  public get stockArticleService(): ArticleService {
    return this.articleService;
  }
  public get stockArticleSerialService(): ArticleSerialService {
    return this.articleSerialService;
  }
  public get stockLabelPrinterHelper(): LabelPrinterHelper {
    return this.labelPrinterHelper;
  }
  public get stockColDefs(): ColDef[] {
    return this.colDefs;
  }
  public stockIsBusyAdding(value: boolean): void {
    this._isBusyAdding.set(value);
  }

  constructor(
    localizationService: LocalizationService,
    docService: StockDocMovementService,
    storageLocationService: StorageLocationService,
    injector: Injector,
    editorHelper: StockDocLineEditorHelperService<
      DocLineUpdatedDto,
      DocLineMovedDto,
      DocLineDeletedDto
    >,
    updateQueue: ObserverQueue,
  ) {
    super(
      localizationService,
      docService,
      storageLocationService,
      injector,
      editorHelper,
      updateQueue,
    );
  }

  protected override initColDefs(): ColDef<any, any>[] {
    if (this._policy == null) {
      this.setLinePolicy();
    }

    const colDefs = super.initColDefs();

    if (this.policy.addPurchaseData) {
      var colIndex = colDefs.findIndex(x => x.field == this.colDef_Quantity.field);
      colDefs.splice(
        colIndex + 1,
        0,
        Object.assign({}, colDefs[colIndex], {
          field: 'quantityInPurchaseUnit',
          headerName: this.localizationService.instant('Doc::QuantityInPurchaseUnit'),
        } as Partial<ColDef>),
      );

      colIndex = colDefs.findIndex(x => x.field == 'articleSalesUnitCode');
      colDefs.splice(
        colIndex + 1,
        0,
        Object.assign({}, colDefs[colIndex], {
          field: 'articlePurchaseUnitCode',
          headerName: this.localizationService.instant('Product::Article:ProductPurchaseUnit'),
          valueFormatter: (params: ValueFormatterParams<any, any>) =>
            params?.data?.articleUsesUnitConversion ? params.value : '',
        } as Partial<ColDef>),
      );

      colIndex = colDefs.findIndex(x => x.field == this.colDef_UnitPrice.field);
      colDefs.splice(
        colIndex + 1,
        0,
        Object.assign({}, this.colDef_UnitPrice, {
          field: 'articlePurchaseUnitPrice',
          headerName: this.localizationService.instant('Doc::PurchaseUnitPrice'),
          editable: params =>
            params.data.articleUsesUnitConversion &&
            (this.colDef_UnitPrice.editable as EditableCallback<any, any>)(params),
        } as Partial<ColDef>),
      );
    }
    return this.policy.enhanceColDefs(colDefs);
  }

  private setLinePolicy(): void {
    if (!this._policy) {
      const router = inject(Router);
      const isEntry = router.url.split('/').includes('stock-entry');
      const isWithdrawal = router.url.split('/').includes('stock-withdrawal');

      if (isEntry) {
        this._policy = new StockDocEntryMovementPolicy(this);

        const parentBtns = this.addActionBtns;
        this.addActionBtns = computed(() => {
          const btns = parentBtns();
          btns[0].shortcutKey = ShortcutKey.controlAdd;
          return btns;
        });
      } else if (isWithdrawal) {
        this._policy = new StockDocWithdrawalMovementPolicy(this);
      } else {
        this._policy = new StockDocMovementPolicy(this);
      }
    }
  }

  protected override getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<any> {
    const fromPolicy = this.policy.onCellValueChanged(event);
    let observable: Observable<any>;
    if (fromPolicy === true && this.policy.addPurchaseData) {
      switch (event.column.getColId()) {
        case 'quantityInPurchaseUnit':
          observable = this.stockDocService.setLinePurchaseQuantity(
            this.document.id,
            this.document.concurrencyStamp,
            event.data.id,
            event.data.quantityInPurchaseUnit,
          );
          break;
        case 'articlePurchaseUnitPrice':
          observable = this.stockDocService.setLinePurchaseUnitPrice(
            this.document.id,
            this.document.concurrencyStamp,
            event.data.id,
            event.data.articlePurchaseUnitPrice,
          );
          break;
        default:
          observable = super.getCellValueChangedObservable(event);
      }
    } else {
      observable = fromPolicy instanceof Observable ? fromPolicy : of(fromPolicy);
    }
    this.policy.beforeSaveLine();
    return observable.pipe(
      map((response: DocLineUpdatedDto) => this.policy.onUnsavedLineChanged(response)),
    );
  }

  protected override addArticleLine(lineFromId?: string, reference?: number): void {
    if (this.lineGrid.grid.rowData.length == 0) {
      super.addLine(this.policy.getAddArticleLineFn(), lineFromId, reference);
      return;
    }

    this.policy.beforeAddArticleLine().subscribeOnce(res => {
      if (res) {
        this.updateLines(res);
      }

      super.addLine(this.policy.getAddArticleLineFn(), lineFromId, reference);
    });
  }

  public beforeSaveDocument(): Observable<DocLineUpdatedDto> {
    return this.policy.beforeSaveDoc();
  }

  protected override onLineAdded(line: DocLineUpdatedDto, setFocus: boolean = true): void {
    super.onLineAdded(line, this.policy.focusOnNewLine());
    this.policy.onLineAdded();
  }
}
